import { thicknessOptions } from '../../../components/CanvasEditor/components/Thickness/thicknessOptions';

const SELECT_STROKE = 'rgba(63, 152, 249, 1)';
const SELECT_FILL = 'rgba(63, 152, 249, 0.05)';
const DEFAULT_THICKNESS_CONTEXT = {
  mode: 'pdf',
  sourceFactor: 1,
};
let thicknessContext = { ...DEFAULT_THICKNESS_CONTEXT };

const isRgba = (color) => typeof color === 'string' && color.includes('rgba(');
const createArrowId = () =>
  `arrow-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const getArrowHeadSize = (strokeWidth) => ({
  width: Math.max(10, (strokeWidth || 1) * 4),
  height: Math.max(14, (strokeWidth || 1) * 5),
});

export const setThicknessContext = (nextContext = {}) => {
  thicknessContext.mode = nextContext.mode;
};

export const getDrawingBounds = (canvas, source, sourceType) => {
  if (sourceType === 'pdf') {
    return {
      width: canvas.getWidth(),
      height: canvas.getHeight(),
    };
  }

  return {
    width: source.width,
    height: source.height,
  };
};

const getArrowParts = (arrow) => {
  if (!arrow || arrow.objectType !== 'arrow') {
    return {
      line: null,
      head: null,
    };
  }

  return {
    line: arrow,
    head:
      arrow.canvas
        ?.getObjects()
        ?.find(
          (obj) =>
            obj.objectType === 'arrow-head' && obj.arrowId === arrow.arrowId
        ) || null,
  };
};

const applyArrowSelectStyle = (arrow) => {
  const { line, head } = getArrowParts(arrow);
  const parts = [line, head].filter(Boolean);

  if (!parts.length) return;

  parts.forEach((part) => {
    if (part.previousStroke === undefined) {
      part.previousStroke = part.stroke;
    }
    if (part.previousFill === undefined) {
      part.previousFill = part.fill;
    }

    if (part.objectType === 'arrow') {
      part.set({ stroke: SELECT_STROKE });
    }

    if (part.objectType === 'arrow-head') {
      part.set({
        stroke: SELECT_STROKE,
        fill: SELECT_STROKE,
      });
    }

    part.setCoords?.();
  });
  line?.setCoords?.();
  if (line) line.dirty = true;
  line?.canvas?.requestRenderAll?.();
};

const resetArrowSelectStyle = (arrow) => {
  const { line, head } = getArrowParts(arrow);
  const parts = [line, head].filter(Boolean);

  if (!parts.length) return;

  parts.forEach((part) => {
    if (part.previousStroke !== undefined) {
      part.set({ stroke: part.previousStroke });
    }
    if (part.previousFill !== undefined) {
      part.set({ fill: part.previousFill });
    }
    part.setCoords?.();
  });

  line?.setCoords?.();
  if (line) line.dirty = true;
  line?.canvas?.requestRenderAll?.();
};

export const syncArrowHeadWithLine = (
  arrowLine,
  options = { updateSize: false, bringToFront: false }
) => {
  const { line, head } = getArrowParts(arrowLine);
  if (!line || !head) return;

  if (options.updateSize || !head.width || !head.height) {
    const size = getArrowHeadSize(line.strokeWidth);
    line.arrowHeadWidth = size.width;
    line.arrowHeadHeight = size.height;
    head.set({
      width: size.width,
      height: size.height,
    });
  }

  const localPoints =
    typeof line.calcLinePoints === 'function'
      ? line.calcLinePoints()
      : {
          x1: line.x1,
          y1: line.y1,
          x2: line.x2,
          y2: line.y2,
        };
  const lineMatrix = line.calcTransformMatrix();
  const start = fabric.util.transformPoint(
    new fabric.Point(localPoints.x1, localPoints.y1),
    lineMatrix
  );
  const end = fabric.util.transformPoint(
    new fabric.Point(localPoints.x2, localPoints.y2),
    lineMatrix
  );
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const ux = length > 0 ? dx / length : 0;
  const uy = length > 0 ? dy / length : 0;
  const tipToCenter = Math.max(
    0,
    (head.height || 0) / 2 - (line.strokeWidth || 0) / 2
  );
  const centerOffset = Math.min(tipToCenter, length);

  head.set({
    left: end.x - ux * centerOffset,
    top: end.y - uy * centerOffset,
    angle: angle + 90,
    fill: line.stroke,
    stroke: line.stroke,
    strokeWidth: line.strokeWidth,
    selectable: false,
    evented: false,
    hasBorders: false,
    hasControls: false,
    objectCaching: false,
    strokeUniform: true,
  });

  head.setCoords?.();
  if (options.bringToFront) {
    head.bringToFront?.();
  }
};

export const createArrowHeadForLine = (arrowLine) => {
  if (!arrowLine) return null;

  const arrowId = arrowLine.arrowId || createArrowId();
  const size = getArrowHeadSize(arrowLine.strokeWidth);

  arrowLine.arrowId = arrowId;
  arrowLine.arrowHeadWidth = size.width;
  arrowLine.arrowHeadHeight = size.height;

  const head = new fabric.Triangle({
    left: arrowLine.x2,
    top: arrowLine.y2,
    originX: 'center',
    originY: 'center',
    width: size.width,
    height: size.height,
    fill: arrowLine.stroke,
    stroke: arrowLine.stroke,
    strokeWidth: arrowLine.strokeWidth,
    selectable: false,
    evented: false,
    hasBorders: false,
    hasControls: false,
    objectType: 'arrow-head',
    pageNumber: arrowLine.pageNumber,
    objectCaching: false,
    strokeUniform: true,
    arrowId,
  });

  syncArrowHeadWithLine(arrowLine, { updateSize: true });

  return head;
};

export const linkArrowObjects = (arrowLine, arrowHead) => {
  if (!arrowLine || !arrowHead) return;

  const arrowId = arrowLine.arrowId || arrowHead.arrowId || createArrowId();

  arrowLine.set({
    objectType: 'arrow',
    arrowId,
    strokeUniform: true,
    objectCaching: false,
  });

  arrowHead.set({
    objectType: 'arrow-head',
    arrowId,
    selectable: false,
    evented: false,
    hasBorders: false,
    hasControls: false,
    objectCaching: false,
    strokeUniform: true,
  });

  if (!arrowLine.__arrowBindingsAttached) {
    arrowLine.on('moving', () => {
      syncArrowHeadWithLine(arrowLine, { bringToFront: true });
      arrowLine.canvas?.requestRenderAll?.();
    });

    arrowLine.on('removed', () => {
      const { head } = getArrowParts(arrowLine);
      if (!head?.canvas || arrowLine.__removingArrowHead) return;

      arrowLine.__removingArrowHead = true;
      head.canvas.remove(head);
      arrowLine.__removingArrowHead = false;
    });

    arrowLine.__arrowBindingsAttached = true;
  }

  syncArrowHeadWithLine(arrowLine, {
    updateSize: true,
    bringToFront: true,
  });
};

export const ensureArrowHead = (arrowLine) => {
  if (!arrowLine || arrowLine.objectType !== 'arrow') return null;

  const existingHead = getArrowParts(arrowLine).head;
  if (existingHead) {
    linkArrowObjects(arrowLine, existingHead);
    return existingHead;
  }

  const head = createArrowHeadForLine(arrowLine);
  if (!head) return null;

  const canvas = arrowLine.canvas;
  if (canvas) {
    canvas.add(head);
  }

  linkArrowObjects(arrowLine, head);

  return head;
};

export const resetObjectStyle = (obj) => {
  if (!obj?.previousStroke && !obj?.previousFill && obj?.objectType !== 'arrow')
    return;

  if (obj.objectType === 'arrow') {
    resetArrowSelectStyle(obj);
    return;
  }

  if (obj.objectType === 'textbox') {
    return;
  }

  if (obj.objectType != 'freehand') {
    obj.set({
      stroke: obj.previousStroke,
      fill: obj.previousFill,
    });
  } else {
    obj.set({
      stroke: obj.previousStroke,
    });
  }
};

export const prepareCanvasSelectionForSerialization = (canvas) => {
  if (!canvas) return null;

  const activeObject = canvas.getActiveObject();
  if (!activeObject) return null;

  if (activeObject.type === 'activeSelection') {
    const objects = activeObject
      .getObjects()
      .filter((obj) => canvas.getObjects().includes(obj));

    objects.forEach((obj) => {
      obj.setCoords?.();
    });

    canvas.discardActiveObject();

    return {
      type: 'activeSelection',
      objects,
    };
  }

  return {
    type: 'single',
    object: activeObject,
    wasEditing:
      activeObject.objectType === 'textbox' && Boolean(activeObject.isEditing),
  };
};

export const selectObjectStyle = (obj) => {
  if (!obj) return;

  if (obj.objectType === 'arrow') {
    applyArrowSelectStyle(obj);
    return;
  }

  obj.set({
    previousStroke: obj.stroke,
    previousFill: obj.fill,
  });

  if (obj.objectType === 'textbox') {
    return;
  }

  if (obj.objectType != 'freehand') {
    obj.set({
      stroke: SELECT_STROKE,
      fill: SELECT_FILL,
    });
  } else {
    obj.set({
      stroke: SELECT_STROKE,
    });
  }
};

export const restoreCanvasSelection = (canvas, selectionState) => {
  if (!canvas || !selectionState) return;

  if (selectionState.type === 'activeSelection') {
    const objects = selectionState.objects?.filter((obj) =>
      canvas.getObjects().includes(obj)
    );

    if (!objects?.length) return;

    if (objects.length === 1) {
      canvas.setActiveObject(objects[0]);
      canvas.requestRenderAll();
      return;
    }

    const selection = new fabric.ActiveSelection(objects, { canvas });
    canvas.setActiveObject(selection);
    disableSelectionControls(selection);
    canvas.requestRenderAll();
    return;
  }

  const { object, wasEditing } = selectionState;
  if (!object || !canvas.getObjects().includes(object)) return;

  canvas.setActiveObject(object);

  if (wasEditing && object.objectType === 'textbox' && object.editable) {
    object.enterEditing?.();
    object.hiddenTextarea?.focus();
  }

  canvas.requestRenderAll();
};

export const colorObjectStyle = (obj, newColor) => {
  const skipFillTypes = ['cloud', 'freehand'];
  const applyArrowColor = (arrow) => {
    const { line, head } = getArrowParts(arrow);
    if (!line) return;

    line.set({
      stroke: newColor,
      previousStroke: newColor,
    });

    head?.set({
      fill: newColor,
      stroke: newColor,
      previousFill: newColor,
      previousStroke: newColor,
    });

    line.set({
      previousStroke: newColor,
      previousFill: newColor,
    });

    line.setCoords?.();
    head?.setCoords?.();
    syncArrowHeadWithLine(line, { bringToFront: true });
    line.dirty = true;
  };

  const applyTextboxColor = (textbox) => {
    if (!textbox) return;

    textbox.set({
      fill: newColor,
      stroke: newColor,
      previousFill: newColor,
      previousStroke: newColor,
    });

    if (typeof textbox.setSelectionStyles === 'function' && textbox.text) {
      textbox.setSelectionStyles({ fill: newColor }, 0, textbox.text.length);
    }

    if (textbox.isEditing && textbox.hiddenTextarea) {
      textbox.hiddenTextarea.style.color = newColor;
    }

    textbox.setCoords?.();
  };

  const getFillOpacity = (type) =>
    ['ellipse', 'polygon', 'rect'].includes(type) ? 0.05 : 0.3;

  const getColor = (oldColor, type) =>
    isRgba(oldColor) ? hexToRgba(newColor, getFillOpacity(type)) : newColor;

  if (obj.type === 'activeSelection') {
    obj.getObjects().forEach((child) => {
      if (child.objectType === 'arrow') {
        applyArrowColor(child);
        return;
      }

      if (child.objectType === 'textbox') {
        applyTextboxColor(child);
        return;
      }

      const stroke = getColor(child.stroke, child.type);
      const setObj = { stroke, previousStroke: stroke };

      if (!skipFillTypes.includes(child.objectType)) {
        setObj.fill = getColor(child.fill, child.type);
        setObj.previousFill = getColor(child.fill, child.type);
      }

      child.set(setObj);
      child.setCoords?.();
    });
  } else {
    if (obj.objectType === 'arrow') {
      applyArrowColor(obj);
      return;
    }

    if (obj.objectType === 'textbox') {
      applyTextboxColor(obj);
      return;
    }

    const previousStroke = getColor(obj.previousStroke, obj.type);
    const setObj = { previousStroke };

    if (!skipFillTypes.includes(obj.objectType)) {
      setObj.previousFill = getColor(obj.previousFill, obj.type);
    }

    obj.set(setObj);
    obj.setCoords?.();
  }
};

export const setObjectThickness = (obj, selectedThickness) => {
  if (!obj) return;

  const applyThickness = (target) => {
    if (target.objectType === 'arrow') {
      const thickness = getThicknessPx(selectedThickness);
      const { line, head } = getArrowParts(target);
      if (!line) return;

      line.set({ strokeWidth: thickness });
      head?.set({
        strokeWidth: Math.max(1, thickness / 2),
      });

      syncArrowHeadWithLine(line, {
        updateSize: true,
        bringToFront: true,
      });

      line.setCoords?.();
      head?.setCoords?.();
      line.dirty = true;
      line.canvas?.requestRenderAll();
      return;
    }

    if (target.objectType === 'textbox') {
      const pdfScaleFactor = target?.canvas?.pdfScaleFactor || 1;
      const fontSize =
        getThicknessPx(selectedThickness, 'textbox') * pdfScaleFactor;

      target.set({ fontSize });
      target.initDimensions?.();
      target.setCoords?.();

      if (target.isEditing && target.hiddenTextarea) {
        target.hiddenTextarea.style.fontSize = `${fontSize}px`;
      }

      target.canvas?.requestRenderAll();
      return;
    }

    const isFreehand = target.objectType === 'freehand';
    const isHighlight = isFreehand && isRgba(target.previousStroke);
    const thickness = getThicknessPx(
      selectedThickness,
      isHighlight ? 'highlighter' : undefined
    );

    target.set({ strokeWidth: thickness });
    target.setCoords?.();
  };

  if (obj.type === 'activeSelection') {
    obj.getObjects().forEach(applyThickness);
  } else {
    applyThickness(obj);
  }
};

export function overrideContainsPoint(obj, canvas) {
  if (!obj || !canvas || obj.objectType !== 'line') return;

  if (!obj._originalContainsPoint) {
    obj._originalContainsPoint = obj.containsPoint;
  }

  obj.containsPoint = function (point, lines, absolute, calculate) {
    return obj._originalContainsPoint.call(
      this,
      point,
      lines,
      absolute,
      calculate
    );
  };
}

export function resetContainsPoint(obj) {
  if (!obj) return;

  if (obj._originalContainsPoint) {
    obj.containsPoint = obj._originalContainsPoint;
    delete obj._originalContainsPoint;
  } else {
    delete obj.containsPoint;
  }
}

export const addControlPointsToLine = (line, canvas, viewer, plan) => {
  if (!line || !['line', 'arrow'].includes(line.objectType)) return;

  const zoom = viewer?.viewport.getZoom() || 5;
  const pixelToMmRatio = 7.874;
  const controlPointRatio = 14;

  const size = (pixelToMmRatio / zoom) * controlPointRatio;
  const maxX = plan?.width ?? canvas?.getWidth?.() ?? 0;
  const maxY = plan?.height ?? canvas?.getHeight?.() ?? 0;
  const isArrow = line.objectType === 'arrow';
  const points = {
    start: { x: line.x1, y: line.y1 },
    end: { x: line.x2, y: line.y2 },
  };

  if (!points) return;

  const createCP = (x, y, updateFn) => {
    const cp = new fabric.Rect({
      left: x - size / 2,
      top: y - size / 2,
      width: size,
      height: size,
      fill: 'rgba(255,0,0,0.05)',
      stroke: '#22a2f8',
      strokeWidth: size / 10,
      hasBorders: false,
      hasControls: false,
      objectCaching: false,
      selectable: true,
      objectType: 'controlpoint',
    });

    cp.on('moving', function () {
      const bounded = {
        x: Math.max(0, Math.min(maxX, this.left + this.width / 2)),
        y: Math.max(0, Math.min(maxY, this.top + this.height / 2)),
      };

      this.set({
        left: bounded.x - this.width / 2,
        top: bounded.y - this.height / 2,
      });

      updateFn(bounded);

      line.set({
        x1: points.start.x,
        y1: points.start.y,
        x2: points.end.x,
        y2: points.end.y,
      });
      updateLinePosition(line);
      line.setCoords();

      if (isArrow) {
        ensureArrowHead(line);
        syncArrowHeadWithLine(line, { bringToFront: true });
      }

      canvas.requestRenderAll();
    });

    return cp;
  };

  const cp1 = createCP(points.start.x, points.start.y, (p) => {
    points.start = p;
  });
  const cp2 = createCP(points.end.x, points.end.y, (p) => {
    points.end = p;
  });

  canvas.add(cp1, cp2);
  cp1.bringToFront();
  cp2.bringToFront();
  line.controlPoints = [cp1, cp2];
};

const updateLinePosition = (line) => {
  line.set({
    left: Math.min(line.x1, line.x2),
    top: Math.min(line.y1, line.y2),
    startLeft: line.left,
    startTop: line.top,
  });
};

export const removeControlPoints = (canvas) => {
  const controlPoints = canvas
    .getObjects()
    .filter((obj) => obj.objectType === 'controlpoint');

  controlPoints.forEach((cp) => {
    canvas.remove(cp);
  });

  canvas.getObjects().forEach((obj) => {
    obj.set('controlPoints', []);
  });

  canvas.requestRenderAll();
};

export const disableSelectionControls = (selection) => {
  if (!selection || selection.type !== 'activeSelection') return;

  selection.set({
    hasControls: false,
    hasBorders: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    lockMovementX: true,
    lockMovementY: true,
    selectable: true,
    // evented: true,
  });

  selection.setCoords();
};

export const rgbaToHex = (rgba) => {
  const m = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return rgba;
  const r = parseInt(m[1], 10).toString(16).padStart(2, '0');
  const g = parseInt(m[2], 10).toString(16).padStart(2, '0');
  const b = parseInt(m[3], 10).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`.toUpperCase();
};

export const hexToRgba = (hex, alpha = 1) => {
  const bigint = parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const findTaskObjectOnCanvas = (canvas, taskID) => {
  if (!canvas || !taskID) return null;

  const objects = canvas.getObjects();

  const taskObj = objects.find((obj) => {
    return obj.objectType === 'task' && obj.taskID == taskID;
  });

  return taskObj || null;
};

export function getThicknessPx(selectedThickness, activeTool) {
  const thicknessObj = thicknessOptions.find(
    (opt) => opt.key === selectedThickness
  );
  if (!thicknessObj) return 1;

  const baseThickness =
    activeTool === 'highlighter'
      ? thicknessObj.highlighterPx
      : thicknessObj[activeTool + 'Px'] || thicknessObj.pencilPx;
  const factor =
    thicknessContext.mode === 'osd' ? thicknessContext.sourceFactor : 0.35;
  return baseThickness * factor;
}
